import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Plus, Trash2, Edit, Save, X, Upload, Image as ImageIcon,
    ChevronUp, ChevronDown, Check, Settings, Hash, AlertCircle, StepBack, Package, Palette
} from 'lucide-react';
import ENV from '../config/env';
import LogoutButton from './LogoutButton';

const API_BASE = ENV.API_BASE;
const IMAGE_BASE_PATH = ENV.IMAGE_BASE_PATH;
const FILE_SERVER_URL = ENV.FILE_SERVER_URL;

// Common technical spec suggestions
const COMMON_SPECS = [
    'MATERIALES', 'TALLAS', 'COLORES', 'PESO', 'DURABILIDAD',
    'PROCESO', 'TIEMPO DE ENTREGA', 'CUIDADOS', 'CAPACIDAD', 'GROSOR',
    'RESISTENCIA', 'GRAMAJE', 'ACABADO', 'IMPERMEABLE', 'RESISTENCIA UV',
    'INSTALACIÓN', 'MEDIDAS', 'FORMATO', 'CANTIDAD MINIMA', 'PAPEL',
    'ACABADOS', 'ADHESIVOS', 'LAMINADO', 'CORTE'
];

// Helper: Generate unique SKU for variants
const generateSku = (productId, color) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    const colorCode = color ? color.substring(0, 3).toUpperCase() : 'VAR';
    return `VAR-${productId}-${colorCode}-${timestamp}-${random}`;
};

// Helper: Check if string is a color hex
const isColorHex = (str) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(str);
};

// Create empty product template
const createEmptyProduct = () => ({
    id: Date.now(),
    name: '',
    category: '',
    subCategory: '',
    brand: '',
    shortDesc: '',
    longDesc: '',
    price: '',
    images: [],
    technicalSpecs: {},
    features: [],
    applications: [],
    variants: []
});

// Create empty variant template
const createEmptyVariant = (productId) => ({
    sku: generateSku(productId, ''),
    color: '',
    images: []
});

// Custom hook: Product data management with API
const useProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serverStatus, setServerStatus] = useState('checking');

    const checkServerStatus = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            setServerStatus(response.ok ? 'connected' : 'disconnected');
            return response.ok;
        } catch {
            setServerStatus('disconnected');
            return false;
        }
    }, []);

    const loadProducts = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/products`);
            if (response.ok) {
                const data = await response.json();
                setProducts(data);
                setError(null);
            } else {
                throw new Error('Failed to load products');
            }
        } catch (err) {
            setError('Error loading products from server');
            if (ENV.IS_DEV) console.error('Error loading products:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const saveProducts = useCallback(async (newProducts) => {
        try {
            const response = await fetch(`${API_BASE}/products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newProducts)
            });

            if (response.ok) {
                setProducts(newProducts);
                setError(null);
                return true;
            } else {
                throw new Error('Failed to save products');
            }
        } catch (err) {
            setError('Error saving products to server');
            if (ENV.IS_DEV) console.error('Error saving products:', err);
            return false;
        }
    }, []);

    const deleteImageFromServer = useCallback(async (imagePath) => {
        const fileName = imagePath.split('/').pop();
        try {
            const response = await fetch(`${API_BASE}/delete/products/${fileName}`, {
                method: 'DELETE'
            });
            return response.ok;
        } catch (error) {
            console.error('Error deleting image:', error);
            return false;
        }
    }, []);

    const uploadImages = useCallback(async (imageDataArray) => {
        try {
            const response = await fetch(`${API_BASE}/upload/products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ images: imageDataArray })
            });

            if (response.ok) {
                const result = await response.json();
                return result.files.map(file => file.path);
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading images:', error);
            throw error;
        }
    }, []);

    useEffect(() => {
        checkServerStatus();
        loadProducts();
    }, [checkServerStatus, loadProducts]);

    const addProduct = useCallback(async (product) => {
        const newProduct = { ...product, id: Date.now() };
        const newProducts = [...products, newProduct];
        const saved = await saveProducts(newProducts);
        return saved ? newProduct : null;
    }, [products, saveProducts]);

    const updateProduct = useCallback(async (updatedProduct, imagesToDelete = []) => {
        if (imagesToDelete.length > 0) {
            await Promise.all(imagesToDelete.map(img => deleteImageFromServer(img)));
        }
        const newProducts = products.map(p => p.id === updatedProduct.id ? updatedProduct : p);
        const saved = await saveProducts(newProducts);
        return saved;
    }, [products, saveProducts, deleteImageFromServer]);

    const deleteProduct = useCallback(async (id) => {
        const productToDelete = products.find(p => p.id === id);
        if (!productToDelete) return false;

        if (!window.confirm(`¿Estás seguro de que quieres eliminar "${productToDelete.name}"? También se eliminarán sus imágenes del servidor.`)) {
            return false;
        }

        const allImages = [...(productToDelete.images || [])];
        productToDelete.variants?.forEach(variant => {
            allImages.push(...(variant.images || []));
        });

        const imageDeletions = allImages.map(img => deleteImageFromServer(img));
        await Promise.all(imageDeletions);

        const newProducts = products.filter(p => p.id !== id);
        const saved = await saveProducts(newProducts);

        if (saved) {
            alert('✅ Producto e imágenes eliminados correctamente');
        }
        return saved;
    }, [products, saveProducts, deleteImageFromServer]);

    const moveProduct = useCallback(async (index, direction) => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === products.length - 1)) {
            return false;
        }
        const newProducts = [...products];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];
        const saved = await saveProducts(newProducts);
        return saved;
    }, [products, saveProducts]);

    return {
        products,
        loading,
        error,
        serverStatus,
        checkServerStatus,
        addProduct,
        updateProduct,
        deleteProduct,
        moveProduct,
        uploadImages,
        deleteImageFromServer
    };
};

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('blob:')) return imagePath;
    if (imagePath.startsWith('http')) return imagePath;
    return `${FILE_SERVER_URL}${imagePath}`;
};

// Custom hook: Variant form management
const useVariantForm = (productId, initialVariant = null, onSave, uploadImages, onCloseModal) => {
    const [variant, setVariant] = useState(() => initialVariant ? { ...initialVariant } : createEmptyVariant(productId));
    const [isUploading, setIsUploading] = useState(false);
    const [tempImages, setTempImages] = useState([]);
    const [removedImages, setRemovedImages] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    // Reset form when initialVariant changes
    useEffect(() => {
        if (initialVariant) {
            setVariant({ ...initialVariant });
        } else {
            setVariant(createEmptyVariant(productId));
        }
        setTempImages([]);
        setRemovedImages([]);
    }, [initialVariant, productId]);

    // Handle input changes
    const handleInputChange = useCallback((e, field) => {
        let value = e.target.value;
        if (field === 'color') {
            value = value.toUpperCase();
        }
        setVariant(prev => ({ ...prev, [field]: value }));
    }, []);

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });
    };

    // Handle image selection
    const handleImageSelect = useCallback(async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const validFiles = files.filter(file =>
                file.type.startsWith('image/') &&
                ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type.toLowerCase())
            );

            if (validFiles.length === 0) {
                alert('Por favor, sube solo archivos de imagen (JPEG, PNG, WebP)');
                return;
            }

            const imagePromises = validFiles.map(file => fileToBase64(file));
            const imageDataArray = await Promise.all(imagePromises);
            setTempImages(prev => [...prev, ...imageDataArray]);
            const tempUrls = validFiles.map(file => URL.createObjectURL(file));
            setVariant(prev => ({
                ...prev,
                images: [...prev.images, ...tempUrls]
            }));
        } catch (error) {
            console.error('Error processing images:', error);
            alert('❌ Error al procesar las imágenes');
        } finally {
            setIsUploading(false);
        }
    }, []);

    // Remove image
    const removeImage = useCallback((index) => {
        const imageToRemove = variant.images[index];
        if (!imageToRemove.startsWith('blob:')) {
            setRemovedImages(prev => [...prev, imageToRemove]);
        }
        if (imageToRemove.startsWith('blob:')) {
            URL.revokeObjectURL(imageToRemove);
        }
        setVariant(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    }, [variant.images]);

    // Handle save - closes modal after saving
    const handleSave = useCallback(async () => {
        if (!variant.color.trim()) {
            alert('Por favor ingresa un color para la variante');
            return;
        }

        setIsUploading(true);
        try {
            let newImagePaths = [];
            if (tempImages.length > 0) {
                newImagePaths = await uploadImages(tempImages);
            }
            const existingImages = variant.images.filter(img =>
                !img.startsWith('blob:') && !removedImages.includes(img)
            );
            const finalImages = [...existingImages, ...newImagePaths];
            const finalVariant = { ...variant, images: finalImages };

            if (!initialVariant) {
                finalVariant.sku = generateSku(productId, finalVariant.color);
            }

            await onSave(finalVariant, removedImages);
            setIsOpen(false);
            if (onCloseModal) {
                onCloseModal();
            }
        } catch (error) {
            console.error('Error saving variant:', error);
            alert('❌ Error al guardar la variante');
        } finally {
            setIsUploading(false);
        }
    }, [variant, tempImages, removedImages, uploadImages, onSave, productId, initialVariant, onCloseModal]);

    // Cleanup blob URLs
    useEffect(() => {
        return () => {
            variant.images.forEach(img => {
                if (img.startsWith('blob:')) {
                    URL.revokeObjectURL(img);
                }
            });
        };
    }, [variant.images]);

    return {
        variant,
        isUploading,
        isOpen,
        setIsOpen,
        handleInputChange,
        handleImageSelect,
        removeImage,
        handleSave
    };
};

// Component: Variant card in product form
const VariantCard = ({ variant, onEdit, onDelete }) => {
    return (
        <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600 hover:border-primary/50 transition-colors">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        {variant.color && isColorHex(variant.color) ? (
                            <div
                                className="w-5 h-5 rounded-full border border-gray-500"
                                style={{ backgroundColor: variant.color }}
                                title={variant.color}
                            />
                        ) : (
                            <Palette size={16} className="text-gray-400" />
                        )}
                        <span className="font-medium text-sm">{variant.color || 'Sin color'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                            <ImageIcon size={12} />
                            <span>{variant.images?.length || 0} imágenes</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => onEdit(variant)}
                        className="p-1.5 bg-blue-600/20 hover:bg-blue-600/40 rounded-lg transition-colors"
                        title="Editar variante"
                    >
                        <Edit size={14} className="text-blue-400" />
                    </button>
                    <button
                        onClick={() => onDelete(variant.sku)}
                        className="p-1.5 bg-red-600/20 hover:bg-red-600/40 rounded-lg transition-colors"
                        title="Eliminar variante"
                    >
                        <Trash2 size={14} className="text-red-400" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Component: Variant form modal (simplified)
const VariantFormModal = ({ isOpen, onClose, form }) => {
    const {
        variant,
        isUploading,
        handleInputChange,
        handleImageSelect,
        removeImage,
        handleSave
    } = form;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Palette size={20} />
                            {variant.sku === 'new' ? 'Nueva Variante' : 'Editar Variante'}
                        </h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Color - Required */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Color *</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={variant.color}
                                    onChange={(e) => handleInputChange(e, 'color')}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: ROJO, AZUL, #FF0000"
                                />
                                {isColorHex(variant.color) && (
                                    <div
                                        className="w-10 h-10 rounded-lg border border-gray-600"
                                        style={{ backgroundColor: variant.color }}
                                    />
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Puedes usar nombre de color o código hexadecimal (ej: #FF0000)
                            </p>
                        </div>

                        {/* Images */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Imágenes de la variante</label>
                            <div className="flex flex-wrap gap-4 mb-4">
                                {variant.images.map((img, index) => (
                                    <div key={index} className="relative group">
                                        <div className="w-20 h-20 bg-gray-700 rounded-lg overflow-hidden">
                                            <img
                                                src={getFullImageUrl(img)}
                                                alt={`Variant ${index}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                        <button
                                            onClick={() => removeImage(index)}
                                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                                {variant.images.length === 0 && (
                                    <div className="w-20 h-20 bg-gray-700 rounded-lg flex items-center justify-center">
                                        <ImageIcon size={30} className="text-gray-600" />
                                    </div>
                                )}
                            </div>
                            <label className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 cursor-pointer w-fit">
                                {isUploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Procesando...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} />
                                        Seleccionar imágenes
                                    </>
                                )}
                                <input
                                    type="file"
                                    multiple
                                    accept="image/jpeg,image/png,image/webp,image/jpg"
                                    onChange={handleImageSelect}
                                    className="hidden"
                                    disabled={isUploading}
                                />
                            </label>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-4 pt-6 border-t border-gray-700">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isUploading}
                                className={`${isUploading ? 'bg-gray-600 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} text-white px-6 py-2 rounded-lg flex items-center gap-2`}
                            >
                                {isUploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Subiendo...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Guardar Variante
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Custom hook: Product form state management
const useProductForm = (initialProduct = null, onSave, uploadImages) => {
    const [product, setProduct] = useState(() => initialProduct ? { ...initialProduct } : createEmptyProduct());
    const [newSpecKey, setNewSpecKey] = useState('');
    const [newSpecValue, setNewSpecValue] = useState('');
    const [newArrayItem, setNewArrayItem] = useState('');
    const [editingSpecIndex, setEditingSpecIndex] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [tempImages, setTempImages] = useState([]);
    const [removedImages, setRemovedImages] = useState([]);
    const [editingVariant, setEditingVariant] = useState(null);
    const [showVariantModal, setShowVariantModal] = useState(false);

    // Close variant modal function
    const closeVariantModal = useCallback(() => {
        setShowVariantModal(false);
        setEditingVariant(null);
    }, []);

    // Variant form hook - now receives closeVariantModal
    const variantForm = useVariantForm(
        product.id,
        editingVariant,
        async (savedVariant, variantRemovedImages) => {
            let updatedVariants;
            if (editingVariant) {
                updatedVariants = product.variants.map(v =>
                    v.sku === savedVariant.sku ? savedVariant : v
                );
            } else {
                updatedVariants = [...(product.variants || []), savedVariant];
            }
            setProduct(prev => ({ ...prev, variants: updatedVariants }));
            setEditingVariant(null);
            setRemovedImages(prev => [...prev, ...variantRemovedImages]);
        },
        uploadImages,
        closeVariantModal
    );

    // Reset form when initialProduct changes
    useEffect(() => {
        if (initialProduct) {
            setProduct({ ...initialProduct });
        } else {
            setProduct(createEmptyProduct());
        }
        setTempImages([]);
        setRemovedImages([]);
        setNewSpecKey('');
        setNewSpecValue('');
        setNewArrayItem('');
        setEditingSpecIndex(null);
        setEditingVariant(null);
    }, [initialProduct]);

    // Handle input changes
    const handleInputChange = useCallback((e, field) => {
        let value = e.target.value;
        if (field === 'category' || field === 'subCategory' || field === 'brand') {
            value = value.toUpperCase();
        }
        if (field === 'name') {
            value = value.toUpperCase();
        }
        setProduct(prev => ({ ...prev, [field]: value }));
    }, []);

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });
    };

    // Handle image selection
    const handleImageSelect = useCallback(async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const validFiles = files.filter(file =>
                file.type.startsWith('image/') &&
                ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type.toLowerCase())
            );

            if (validFiles.length === 0) {
                alert('Por favor, sube solo archivos de imagen (JPEG, PNG, WebP)');
                return;
            }

            const imagePromises = validFiles.map(file => fileToBase64(file));
            const imageDataArray = await Promise.all(imagePromises);
            setTempImages(prev => [...prev, ...imageDataArray]);
            const tempUrls = validFiles.map(file => URL.createObjectURL(file));
            setProduct(prev => ({
                ...prev,
                images: [...prev.images, ...tempUrls]
            }));
        } catch (error) {
            console.error('Error processing images:', error);
            alert('❌ Error al procesar las imágenes');
        } finally {
            setIsUploading(false);
        }
    }, []);

    // Remove image from product
    const removeImage = useCallback((index) => {
        const imageToRemove = product.images[index];
        if (!imageToRemove.startsWith('blob:')) {
            setRemovedImages(prev => [...prev, imageToRemove]);
        }
        if (imageToRemove.startsWith('blob:')) {
            URL.revokeObjectURL(imageToRemove);
            const serverImageCount = product.images.filter((img, i) =>
                i < index && !img.startsWith('blob:')
            ).length;
            const tempImageIndex = index - serverImageCount;
            if (tempImageIndex >= 0 && tempImageIndex < tempImages.length) {
                setTempImages(prev => prev.filter((_, i) => i !== tempImageIndex));
            }
        }
        setProduct(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    }, [product.images, tempImages]);

    // Add technical specification
    const addTechnicalSpec = useCallback(() => {
        if (!newSpecKey.trim()) {
            alert('Por favor ingresa un nombre para la especificación');
            return;
        }
        const key = newSpecKey.trim();
        const value = newSpecValue.trim();
        setProduct(prev => ({
            ...prev,
            technicalSpecs: { ...prev.technicalSpecs, [key]: value }
        }));
        setNewSpecKey('');
        setNewSpecValue('');
    }, [newSpecKey, newSpecValue]);

    // Edit technical specification
    const editTechnicalSpec = useCallback((oldKey, newKey, newValue) => {
        if (!newKey || !newKey.trim()) return;
        const finalKey = newKey.trim();
        const specs = { ...product.technicalSpecs };
        delete specs[oldKey];
        specs[finalKey] = newValue;
        setProduct(prev => ({ ...prev, technicalSpecs: specs }));
        setEditingSpecIndex(null);
    }, [product.technicalSpecs]);

    // Remove technical specification
    const removeTechnicalSpec = useCallback((key) => {
        const specs = { ...product.technicalSpecs };
        delete specs[key];
        setProduct(prev => ({ ...prev, technicalSpecs: specs }));
    }, [product.technicalSpecs]);

    // Add array item
    const addArrayItem = useCallback((arrayName, value) => {
        if (!value.trim()) return;
        setProduct(prev => ({
            ...prev,
            [arrayName]: [...prev[arrayName], value.trim()]
        }));
        setNewArrayItem('');
    }, []);

    // Remove array item
    const removeArrayItem = useCallback((arrayName, index) => {
        setProduct(prev => ({
            ...prev,
            [arrayName]: prev[arrayName].filter((_, i) => i !== index)
        }));
    }, []);

    // Update array item
    const updateArrayItem = useCallback((arrayName, index, value) => {
        setProduct(prev => {
            const newArray = [...prev[arrayName]];
            newArray[index] = value;
            return { ...prev, [arrayName]: newArray };
        });
    }, []);

    // Open variant modal
    const openVariantModal = useCallback((variant = null) => {
        setEditingVariant(variant);
        setShowVariantModal(true);
    }, []);

    // Delete variant
    const deleteVariant = useCallback((sku) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta variante?')) {
            return;
        }
        const variantToDelete = product.variants?.find(v => v.sku === sku);
        if (variantToDelete?.images) {
            setRemovedImages(prev => [...prev, ...variantToDelete.images]);
        }
        setProduct(prev => ({
            ...prev,
            variants: prev.variants?.filter(v => v.sku !== sku) || []
        }));
    }, [product.variants]);

    // Validate product before save
    const validateProduct = useCallback(() => {
        if (!product.name.trim() || !product.category.trim()) {
            alert('Por favor completa al menos el nombre y categoría del producto');
            return false;
        }
        return true;
    }, [product]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!validateProduct()) return;
        setIsUploading(true);
        try {
            let newImagePaths = [];
            if (tempImages.length > 0) {
                newImagePaths = await uploadImages(tempImages);
            }
            const existingImages = product.images.filter(img =>
                !img.startsWith('blob:') && !removedImages.includes(img)
            );
            const finalImages = [...existingImages, ...newImagePaths];
            const finalProduct = { ...product, images: finalImages };
            await onSave(finalProduct, removedImages);
        } catch (error) {
            console.error('Error saving product:', error);
            alert('❌ Error al guardar el producto');
        } finally {
            setIsUploading(false);
        }
    }, [product, tempImages, removedImages, uploadImages, onSave, validateProduct]);

    // Cleanup blob URLs
    useEffect(() => {
        return () => {
            product.images.forEach(img => {
                if (img.startsWith('blob:')) {
                    URL.revokeObjectURL(img);
                }
            });
        };
    }, [product.images]);

    return {
        product,
        newSpecKey,
        newSpecValue,
        newArrayItem,
        editingSpecIndex,
        isUploading,
        showVariantModal,
        editingVariant,
        variantForm,
        setNewSpecKey,
        setNewSpecValue,
        setNewArrayItem,
        setEditingSpecIndex,
        handleInputChange,
        handleImageSelect,
        removeImage,
        addTechnicalSpec,
        editTechnicalSpec,
        removeTechnicalSpec,
        addArrayItem,
        removeArrayItem,
        updateArrayItem,
        openVariantModal,
        closeVariantModal,
        deleteVariant,
        handleSave
    };
};

// Component: Product card in manager
const ProductCard = ({ product, index, products, onEdit, onDelete, onMove }) => {
    const { images, name, category, price, features, technicalSpecs, variants } = product;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-primary/50 transition-colors"
        >
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onMove(index, 'up')}
                        disabled={index === 0}
                        className={`p-1 rounded ${index === 0 ? 'text-gray-600' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                        <ChevronUp size={16} />
                    </button>
                    <button
                        onClick={() => onMove(index, 'down')}
                        disabled={index === products.length - 1}
                        className={`p-1 rounded ${index === products.length - 1 ? 'text-gray-600' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                        <ChevronDown size={16} />
                    </button>
                    <span className="text-sm text-gray-400">#{index + 1}</span>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => onEdit(product)} className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
                        <Edit size={16} />
                    </button>
                    <button onClick={() => onDelete(product.id)} className="p-2 bg-red-600 hover:bg-red-700 rounded-lg">
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
            <div className="p-4">
                <div className="h-40 mb-4 rounded-lg overflow-hidden bg-gray-700">
                    {images && images.length > 0 ? (
                        <img
                            src={getFullImageUrl(images[0])}
                            alt={name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={40} className="text-gray-600" />
                        </div>
                    )}
                </div>
                <h3 className="text-lg font-bold mb-2 line-clamp-1">{name || 'Sin nombre'}</h3>
                <div className="flex items-center gap-2 mb-2">
                    <span className="bg-primary/20 text-primary text-xs px-2 py-1 rounded">
                        {category || 'Sin categoría'}
                    </span>
                    <span className="text-sm text-yellow-400">{price || '$0'}</span>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                    <div className="flex items-center gap-2">
                        <Check size={12} /> {features?.length || 0} características
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <Settings size={12} /> {Object.keys(technicalSpecs || {}).length} especificaciones
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <ImageIcon size={12} /> {images?.length || 0} imágenes
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <Package size={12} /> {variants?.length || 0} variantes de color
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Component: Product form modal
const ProductFormModal = ({ isOpen, product, onClose, onSave, mode, uploadImages }) => {
    const {
        product: formProduct,
        newSpecKey, newSpecValue, newArrayItem, editingSpecIndex, isUploading,
        showVariantModal, editingVariant, variantForm,
        setNewSpecKey, setNewSpecValue, setNewArrayItem, setEditingSpecIndex,
        handleInputChange, handleImageSelect, removeImage,
        addTechnicalSpec, editTechnicalSpec, removeTechnicalSpec,
        addArrayItem, removeArrayItem, updateArrayItem,
        openVariantModal, closeVariantModal, deleteVariant,
        handleSave
    } = useProductForm(product, onSave, uploadImages);

    const formatSpecKey = (key) => {
        return key.charAt(0).toUpperCase() + key.slice(1);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">
                            {mode === 'new' ? 'Nuevo Producto' : 'Editar Producto'}
                        </h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Nombre *</label>
                                <input
                                    type="text"
                                    value={formProduct.name}
                                    onChange={(e) => handleInputChange(e, 'name')}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Nombre del producto"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Categoría *</label>
                                <input
                                    type="text"
                                    value={formProduct.category}
                                    onChange={(e) => handleInputChange(e, 'category')}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: IMPRESIÓN, PERSONALIZACIÓN"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Subcategoría</label>
                                <input
                                    type="text"
                                    value={formProduct.subCategory || ''}
                                    onChange={(e) => handleInputChange(e, 'subCategory')}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: TEXTIL, PROMOCIONAL"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Marca</label>
                                <input
                                    type="text"
                                    value={formProduct.brand || ''}
                                    onChange={(e) => handleInputChange(e, 'brand')}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Marca del producto"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Precio</label>
                                <input
                                    type="text"
                                    value={formProduct.price}
                                    onChange={(e) => handleInputChange(e, 'price')}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="$250 - $450"
                                />
                            </div>
                        </div>

                        {/* Descriptions */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Descripción corta</label>
                            <input
                                type="text"
                                value={formProduct.shortDesc}
                                onChange={(e) => handleInputChange(e, 'shortDesc')}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                placeholder="Descripción breve"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Descripción larga</label>
                            <textarea
                                value={formProduct.longDesc}
                                onChange={(e) => handleInputChange(e, 'longDesc')}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-32 focus:ring-2 focus:ring-primary focus:border-transparent"
                                placeholder="Descripción detallada del producto"
                            />
                        </div>

                        {/* Images */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Imágenes del producto</label>
                            <div className="flex flex-wrap gap-4 mb-4">
                                {formProduct.images.map((img, index) => (
                                    <div key={index} className="relative group">
                                        <div className="w-24 h-24 bg-gray-700 rounded-lg overflow-hidden">
                                            <img
                                                src={getFullImageUrl(img)}
                                                alt={`Preview ${index}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                        <button
                                            onClick={() => removeImage(index)}
                                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                                {formProduct.images.length === 0 && (
                                    <div className="w-24 h-24 bg-gray-700 rounded-lg flex items-center justify-center">
                                        <ImageIcon size={30} className="text-gray-600" />
                                    </div>
                                )}
                            </div>
                            <label className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 cursor-pointer w-fit">
                                {isUploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Procesando...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} />
                                        Seleccionar imágenes
                                    </>
                                )}
                                <input
                                    type="file"
                                    multiple
                                    accept="image/jpeg,image/png,image/webp,image/jpg"
                                    onChange={handleImageSelect}
                                    className="hidden"
                                    disabled={isUploading}
                                />
                            </label>
                            <p className="text-sm text-gray-400 mt-2">
                                Las imágenes se subirán al servidor cuando guardes el producto
                            </p>
                        </div>

                        {/* Variants Section */}
                        <div className="border-t border-gray-700 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <Palette size={20} />
                                    Variantes de Color
                                </h3>
                                <button
                                    onClick={() => openVariantModal()}
                                    className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                                >
                                    <Plus size={16} />
                                    Agregar Variante
                                </button>
                            </div>

                            {formProduct.variants && formProduct.variants.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {formProduct.variants.map((variant) => (
                                        <VariantCard
                                            key={variant.sku}
                                            variant={variant}
                                            onEdit={openVariantModal}
                                            onDelete={deleteVariant}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-gray-700/30 rounded-lg border border-gray-600 border-dashed">
                                    <Palette size={32} className="text-gray-500 mx-auto mb-2" />
                                    <p className="text-gray-400 text-sm">
                                        No hay variantes de color. Agrega una para mostrar diferentes opciones.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Features */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium">Características</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newArrayItem}
                                        onChange={(e) => setNewArrayItem(e.target.value)}
                                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="Nueva característica"
                                        onKeyPress={(e) => e.key === 'Enter' && addArrayItem('features', newArrayItem)}
                                    />
                                    <button
                                        onClick={() => addArrayItem('features', newArrayItem)}
                                        className="bg-primary hover:bg-primary/80 text-white px-3 py-1 rounded-lg text-sm"
                                    >
                                        Agregar
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {formProduct.features.map((feature, index) => (
                                    <div key={index} className="flex gap-2 items-center">
                                        <Hash size={14} className="text-gray-400 flex-shrink-0" />
                                        <input
                                            type="text"
                                            value={feature}
                                            onChange={(e) => updateArrayItem('features', index, e.target.value)}
                                            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                        <button
                                            onClick={() => removeArrayItem('features', index)}
                                            className="bg-red-600 hover:bg-red-700 text-white px-3 rounded-lg py-2"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Technical Specifications */}
                        <div className="border-t border-gray-700 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <Settings size={20} />
                                    Especificaciones Técnicas
                                </h3>
                                <div className="text-sm text-gray-400">
                                    {Object.keys(formProduct.technicalSpecs || {}).length} especificaciones
                                </div>
                            </div>

                            <div className="grid md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-700/30 rounded-lg">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Nombre</label>
                                    <input
                                        type="text"
                                        value={newSpecKey}
                                        onChange={(e) => setNewSpecKey(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="Ej: material, tamaño, etc."
                                        list="common-specs"
                                    />
                                    <datalist id="common-specs">
                                        {COMMON_SPECS.map(spec => <option key={spec} value={spec} />)}
                                    </datalist>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Valor</label>
                                    <input
                                        type="text"
                                        value={newSpecValue}
                                        onChange={(e) => setNewSpecValue(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="Ej: Algodón 100%, 10x20cm, etc."
                                        onKeyPress={(e) => e.key === 'Enter' && addTechnicalSpec()}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={addTechnicalSpec}
                                        className="w-full bg-primary hover:bg-primary/80 text-white py-2 rounded-lg"
                                    >
                                        Agregar Especificación
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {Object.entries(formProduct.technicalSpecs || {}).map(([key, value], index) => (
                                    editingSpecIndex === index ? (
                                        <div key={key} className="grid md:grid-cols-3 gap-4 p-4 bg-gray-700/50 rounded-lg">
                                            <div>
                                                <label className="block text-sm font-medium mb-2">Nombre</label>
                                                <input
                                                    type="text"
                                                    defaultValue={key}
                                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2"
                                                    id={`edit-key-${index}`}
                                                    autoFocus
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-2">Valor</label>
                                                <input
                                                    id={`edit-value-${index}`}
                                                    type="text"
                                                    defaultValue={value}
                                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2"
                                                />
                                            </div>
                                            <div className="flex gap-2 items-end">
                                                <button
                                                    onClick={() => {
                                                        const newKey = document.getElementById(`edit-key-${index}`).value;
                                                        const newValue = document.getElementById(`edit-value-${index}`).value;
                                                        editTechnicalSpec(key, newKey, newValue);
                                                    }}
                                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg"
                                                >
                                                    Guardar
                                                </button>
                                                <button
                                                    onClick={() => setEditingSpecIndex(null)}
                                                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg"
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={key} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 group">
                                            <div className="flex-1">
                                                <div className="font-medium text-primary">
                                                    {key}
                                                </div>
                                                <div className="text-sm text-gray-300 mt-1">
                                                    {Array.isArray(value) ? value.join(', ') : value}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => setEditingSpecIndex(index)} className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
                                                    <Edit size={14} />
                                                </button>
                                                <button onClick={() => removeTechnicalSpec(key)} className="p-2 bg-red-600 hover:bg-red-700 rounded-lg">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>

                        {/* Applications */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium">Aplicaciones / Usos</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newArrayItem}
                                        onChange={(e) => setNewArrayItem(e.target.value)}
                                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-sm"
                                        placeholder="Nueva aplicación"
                                        onKeyPress={(e) => e.key === 'Enter' && addArrayItem('applications', newArrayItem)}
                                    />
                                    <button
                                        onClick={() => addArrayItem('applications', newArrayItem)}
                                        className="bg-primary hover:bg-primary/80 text-white px-3 py-1 rounded-lg text-sm"
                                    >
                                        Agregar
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {formProduct.applications.map((app, index) => (
                                    <div key={index} className="flex gap-2 items-center">
                                        <Hash size={14} className="text-gray-400 flex-shrink-0" />
                                        <input
                                            type="text"
                                            value={app}
                                            onChange={(e) => updateArrayItem('applications', index, e.target.value)}
                                            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                        <button
                                            onClick={() => removeArrayItem('applications', index)}
                                            className="bg-red-600 hover:bg-red-700 text-white px-3 rounded-lg py-2"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end gap-4 pt-6 border-t border-gray-700">
                            <button onClick={onClose} className="px-6 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isUploading}
                                className={`${isUploading ? 'bg-gray-600 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} text-white px-6 py-2 rounded-lg flex items-center gap-2`}
                            >
                                {isUploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Subiendo imágenes...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        {mode === 'new' ? 'Guardar Producto' : 'Actualizar Producto'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Variant Modal */}
            <VariantFormModal
                isOpen={showVariantModal}
                onClose={closeVariantModal}
                form={variantForm}
            />
        </div>
    );
};

// Main ProductsManager Component
const ProductsManager = () => {
    const { products, loading, error, serverStatus, addProduct, updateProduct, deleteProduct, moveProduct, uploadImages } = useProducts();
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [modalKey, setModalKey] = useState(0);

    const exportProducts = () => {
        const dataStr = JSON.stringify(products, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const exportFileDefaultName = 'productsData.json';
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const importProducts = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const importedProducts = JSON.parse(event.target.result);
                if (Array.isArray(importedProducts)) {
                    const response = await fetch(`${API_BASE}/products`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(importedProducts)
                    });

                    if (response.ok) {
                        alert('✅ Productos importados exitosamente!');
                        window.location.reload();
                    } else {
                        alert('❌ Error al guardar productos');
                    }
                } else {
                    alert('El archivo no contiene un array de productos válido');
                }
            } catch (error) {
                alert('Error al leer el archivo JSON');
                if (ENV.IS_DEV) console.error('Import error:', error);
            }
        };
        reader.readAsText(file);
    };

    const handleCloseModal = () => {
        setIsAddingNew(false);
        setEditingProduct(null);
        setModalKey(prev => prev + 1);
    };

    const handleOpenNewModal = () => {
        setEditingProduct(null);
        setIsAddingNew(true);
        setModalKey(prev => prev + 1);
    };

    const handleOpenEditModal = (product) => {
        setEditingProduct(product);
        setIsAddingNew(false);
        setModalKey(prev => prev + 1);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Cargando productos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                        Gestor de Productos
                    </h1>
                    <p className="text-gray-300">
                        Administra los detalles de los productos y sus variantes de color
                    </p>

                    <div className={`mt-4 inline-block px-4 py-2 rounded-full ${serverStatus === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                        {serverStatus === 'connected' ? '✅ Servidor de productos conectado' : '❌ Servidor de productos desconectado'}
                    </div>

                    {error && (
                        <div className="mt-2 text-red-400 text-sm">
                            ⚠️ {error}
                        </div>
                    )}
                </motion.div>

                {/* Controls */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div className="flex gap-2">
                        <button
                            onClick={exportProducts}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                        >
                            <Save size={20} />
                            Exportar JSON
                        </button>
                        <label className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 cursor-pointer">
                            <Upload size={20} />
                            Importar JSON
                            <input
                                type="file"
                                accept=".json"
                                onChange={importProducts}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleOpenNewModal}
                            className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Nuevo Producto
                        </button>
                        <button
                            onClick={() => window.history.back()}
                            className={`flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/30 text-white rounded-lg smooth-transition`}
                            title="Regresar"
                        >
                            <StepBack size={18} />
                            <span>Regresar</span>
                        </button>
                        <LogoutButton />
                    </div>
                </div>

                {/* Products List */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product, index) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            index={index}
                            products={products}
                            onEdit={handleOpenEditModal}
                            onDelete={deleteProduct}
                            onMove={moveProduct}
                        />
                    ))}
                </div>

                {/* Empty State */}
                {products.length === 0 && !isAddingNew && (
                    <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
                        <div className="text-gray-400 mb-4">
                            <AlertCircle size={48} className="mx-auto" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">No hay productos</h3>
                        <p className="text-gray-400 mb-6">
                            Comienza agregando tu primer producto
                        </p>
                        <button
                            onClick={handleOpenNewModal}
                            className="bg-primary hover:bg-primary/80 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto"
                        >
                            <Plus size={20} />
                            Agregar Primer Producto
                        </button>
                    </div>
                )}

                {/* Add Product Modal */}
                <ProductFormModal
                    key={`new-${modalKey}`}
                    isOpen={isAddingNew}
                    product={null}
                    onClose={handleCloseModal}
                    onSave={async (newProduct, imagesToDelete) => {
                        const saved = await addProduct(newProduct);
                        if (saved) {
                            handleCloseModal();
                        }
                    }}
                    mode="new"
                    uploadImages={uploadImages}
                />

                {/* Edit Product Modal */}
                <ProductFormModal
                    key={`edit-${modalKey}`}
                    isOpen={!!editingProduct}
                    product={editingProduct}
                    onClose={handleCloseModal}
                    onSave={async (updatedProduct, imagesToDelete) => {
                        const saved = await updateProduct(updatedProduct, imagesToDelete);
                        if (saved) {
                            handleCloseModal();
                        }
                    }}
                    mode="edit"
                    uploadImages={uploadImages}
                />
            </div>
        </div>
    );
};

export default ProductsManager;